import { createContext, useContext } from "react";

import type { GlobalModalProps } from ".";

/**
 * Context for handling global modals
 */
export type GlobalModalContext = {
  children: JSX.Element | null;
} & GlobalModalProps;

export const context = createContext(
  null as unknown as (
    state:
      | GlobalModalContext
      | ((current: GlobalModalContext) => GlobalModalContext),
  ) => void,
);

export type UseGlobalModalProps = { close: () => void };

/**
 * Hook that allows you to trigger modals globally without
 * mounting them in a component
 */
export const useGlobalModal = <T extends UseGlobalModalProps>(
  Comp: React.FC<T>,
  {
    maxWidth,
    closeOnInteractOutside,
    disablePadding,
  }: Omit<GlobalModalProps, "open"> = {},
): {
  open: Omit<T, keyof UseGlobalModalProps> extends Record<string, never>
    ? () => void
    : (props: Omit<T, keyof UseGlobalModalProps>) => void;
  close: () => void;
} => {
  const setModal = useContext(context);

  const close = () => {
    setModal((current) => ({
      ...current,
      open: false,
    }));
  };

  return {
    close,
    open(...args: unknown[]) {
      setModal({
        open: true,
        maxWidth,
        closeOnInteractOutside,
        disablePadding,
        // TypeScript has a challenge evaluating this typing, but it is correct
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        children: <Comp {...args[0]} close={close} />,
      });
    },
  };
};
